import React, { createContext, useState, useEffect, ReactNode } from "react";

interface ResponsiveContextProps {
  /** Indicates if the viewport width is considered mobile (≤ 768 px). */
  isMobile: boolean;
}

/** Context providing a simple mobile‑first responsive flag. */
export const ResponsiveContext = createContext<ResponsiveContextProps>({
  isMobile: false,
});

interface ProviderProps {
  /** Child components that will consume the responsive flag. */
  children: ReactNode;
}

/**
 * ResponsiveProvider reads breakpoint values from CSS variables
 * (see src/styles/breakpoints.css) and updates `isMobile` on resize.
 *
 * Per Section 5 (Responsive Design) of the developer guide we keep the
 * logic lightweight and avoid external libraries.
 */
export const ResponsiveProvider: React.FC<ProviderProps> = ({ children }) => {
  const getMobileBreakpoint = () => {
    const root = window.getComputedStyle(document.documentElement);
    const value =
      root.getPropertyValue("--breakpoint-mobile").trim() || "768px";
    return parseInt(value, 10);
  };
  const [isMobile, setIsMobile] = useState<boolean>(
    window.innerWidth <= getMobileBreakpoint(),
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= getMobileBreakpoint());
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <ResponsiveContext.Provider value={{ isMobile }}>
      {children}
    </ResponsiveContext.Provider>
  );
};
